import { Box, Divider, Flex, Icon, Link, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaCalendarAlt, FaIdCard, FaLink, FaMapMarkerAlt, FaUsers } from "react-icons/fa";
import { OrgPublicProfileDto, OrganizationEntryDto } from "../../../../autogen/bff-api";
import { displayOrganizationSize } from "../../../../common/formatting/displayOrganizationSize";
import { useBrregData, useFinancialData } from "../../../../common/services/brregService";
import { Financials } from "./Financials";

interface Props {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
}

export const Details = ({ organization, organizationEntry }: Props) => {
  const { t } = useTranslation();

  const orgData = organization || organizationEntry || null;

  if (!orgData) throw new Error("No organization data");

  const showFinancialData = !!orgData.organizationNumber;

  const {
    data: brregData,
    isLoading: isLoadingBrregData,
    error,
  } = useBrregData(orgData.organizationNumber, orgData.country.alpha3Code);

  const {
    data: financialData,
    isLoading: isLoadingFinancials,
    error: financialError,
  } = useFinancialData(orgData.organizationNumber, orgData.country.alpha3Code);

  const latestFinancialData = showFinancialData && financialData?.length ? financialData[0] : null;

  return (
    <Flex direction="column" gap={6}>
      <Flex gap={6}>
        <Flex
          width={{ base: "full", lg: "50%" }}
          flexDirection="column"
          alignItems="start"
          justifyContent="start"
          borderRadius="lg"
          backgroundColor="smBackground"
          border="1px solid"
          borderColor="smBorder"
          p="6"
        >
          <Box width="full">
            <Text fontSize={"md"} fontWeight="bold" mb={3}>
              {t("Company Details")}
            </Text>
            {orgData.organizationNumber && (
              <Flex mt="5px">
                <Icon as={FaIdCard} w="15px" h="15px" mt="4px" color="smMuted" />
                <Text fontSize={"sm"} color="smMuted" ml="10px">
                  {orgData.organizationNumber}
                </Text>
              </Flex>
            )}
            <Flex mt="5px">
              <Icon as={FaMapMarkerAlt} w="15px" h="15px" mt="4px" color="smMuted" />
              <Text fontSize={"sm"} color="smMuted" ml="10px">
                {orgData.country.name}
              </Text>
            </Flex>
            {organization?.size && (
              <Flex mt="5px">
                <Icon as={FaUsers} w="15px" h="15px" mt="4px" color="smMuted" />
                <Text fontSize={"sm"} color="smMuted" ml="10px">
                  {displayOrganizationSize(organization.size)}
                </Text>
              </Flex>
            )}
            {isLoadingBrregData && (
              <Flex mt="10px" alignItems="center">
                <Spinner size="sm" color="smMuted" />
                <Text fontSize={"sm"} color="smMuted" ml="10px">
                  {t("Loading company details")}
                </Text>
              </Flex>
            )}
            {error && (
              <Text fontSize={"sm"} color="red.500" mt="10px">
                {error}
              </Text>
            )}
            {!isLoadingBrregData && brregData && (
              <>
                {brregData.forretningsadresse && (
                  <Flex mt="5px" alignItems="flex-start">
                    <Icon as={FaBuilding} w="15px" h="15px" mt="4px" color="smMuted" />
                    <Box fontSize={"sm"} color="smMuted" ml="10px">
                      {brregData.forretningsadresse.adresse?.map((line, i) => <Text key={i}>{line}</Text>)}
                      <Text>
                        {brregData.forretningsadresse.postnummer} {brregData.forretningsadresse.poststed}
                      </Text>
                    </Box>
                  </Flex>
                )}
                {brregData.antallAnsatte > 0 && (
                  <Flex mt="5px">
                    <Icon as={FaUsers} w="15px" h="15px" mt="4px" color="smMuted" />
                    <Text fontSize={"sm"} color="smMuted" ml="10px">
                      {t("Employees")}: {brregData.antallAnsatte}
                    </Text>
                  </Flex>
                )}
                {brregData.stiftelsesdato && (
                  <Flex mt="5px">
                    <Icon as={FaCalendarAlt} w="15px" h="15px" mt="4px" color="smMuted" />
                    <Text fontSize={"sm"} color="smMuted" ml="10px">
                      {t("Founded")}: {new Date(brregData.stiftelsesdato).toLocaleDateString()}
                    </Text>
                  </Flex>
                )}
                {brregData.naeringskode1 && (
                  <Flex mt="5px">
                    <Icon as={FaIdCard} w="15px" h="15px" mt="4px" color="smMuted" />
                    <Text fontSize={"sm"} color="smMuted" ml="10px">
                      {t("Business code")}: {brregData.naeringskode1.kode} - {brregData.naeringskode1.beskrivelse}
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {organization?.homePageUrl && (
              <Flex mt="5px">
                <Icon as={FaLink} w="13px" h="15px" mt="6px" ml="1px" color="smMuted" />
                <Text ml="11px" fontSize={"sm"}>
                  <Link
                    color="smPrimary"
                    href={
                      organization.homePageUrl.indexOf("https://") === -1
                        ? `https://${organization.homePageUrl}`
                        : organization.homePageUrl
                    }
                    target={"_blank"}
                  >
                    {organization.homePageUrl}
                  </Link>
                </Text>
              </Flex>
            )}
          </Box>
        </Flex>
        <Flex display={{ base: "none", lg: "flex" }} width="50%"></Flex>
      </Flex>
      <Flex flexDirection="column" alignItems="start" justifyContent="start" borderRadius="lg" width="full">
        <Box width="full">
          <Text fontSize="md" fontWeight="bold" pb="2">
            {t("Financial Information")}
            {latestFinancialData?.regnskapsperiode?.fraDato &&
              ` (${new Date(latestFinancialData.regnskapsperiode.fraDato).getFullYear()})`}
          </Text>
          <Divider borderColor="gray.300" />
          {isLoadingFinancials && (
            <Flex alignItems="center" pt="6">
              <Spinner size="sm" color="smMuted" />
              <Text fontSize={"sm"} color="smMuted" ml="10px">
                {t("Loading financial data")}
              </Text>
            </Flex>
          )}
          {financialError && (
            <Text fontSize={"sm"} color="red.500" pt="6">
              {financialError}
            </Text>
          )}
          {!isLoadingFinancials && latestFinancialData && <Financials financialData={latestFinancialData} />}
          {!latestFinancialData && !isLoadingFinancials && !financialError && (
            <Text fontSize="sm" color="smMuted">
              {t("No financial data available")}
            </Text>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
